import React, { useEffect, useState } from 'react';
import FidoLogo from 'src/frontend-common/components/Logo';
import ChangePassword from 'src/frontend-common/components/Icons/ChangePassword';
import Column from 'src/frontend-common/components/Column';
import { Box, Checkbox, FormControlLabel, styled, TextField, Typography, useTheme } from '@mui/material';
import { CustomButton } from 'src/pages/Login/forms/styledFormComponents';
import { useAuthMFAMutation, useLoginUserMutation } from 'src/redux/services/auth';
import { Error } from '@mui/icons-material';
import { setAccessToken } from 'src/frontend-common/localstorage';

const Container = styled('div')(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(5),
  maxWidth: '860px',
  alignSelf: 'center',
  [theme.breakpoints.up('xl')]: {
    padding: theme.spacing(15),
  },
}));

const Spacer = styled(Column)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 32,
  lineHeight: 1.2,
  color: theme.palette.primary.main,
  marginBottom: 20,
  textAlign: 'center',
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  lineHeight: 1.1,
  color: theme.palette.text.primary,
  textAlign: 'center',
}));

const VerificationCodePage: React.FC<{
  payload: Record<string, string>;
}> = ({ payload }) => {
  const [smsCode, setSmsCode] = useState<string[]>([]);
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [loginUser, { isError: isErrorLoginUser, error: loginUserError, data: loginData }] = useLoginUserMutation();
  const [isRateLimitError, setIsRateLimitError] = useState<boolean>(false);
  const [disableResend, setDisableResend] = useState<boolean>(false);
  const [sendMFA, { isSuccess, data, isError }] = useAuthMFAMutation();
  const [timeToReset, setTimeToReset] = React.useState<string>();
  const [idToFocus, setIdToFocus] = React.useState<number>(0);
  const [newSessionToken, setNewSessionToken] = React.useState<string>();

  const theme = useTheme();

  useEffect(() => {
    setNewSessionToken(loginData?.session);
  }, [loginData]);
  useEffect(() => {
    if (isSuccess && data) {
      setAccessToken(data.token);
      if (rememberMe) {
        const username = payload['username'];
        localStorage.setItem(`${username}_device_key`, data.device_key);
        localStorage.setItem(`${username}_device_group_key`, data.device_group_key);
        localStorage.setItem(`${username}_device_password`, data.device_password);
      }
      const setUrl = (window.location.href = '/#access_token=' + data.token);
      setTimeout(setUrl, 500);
    }
  }, [data]);

  useEffect(() => {
    if (isErrorLoginUser && loginUserError) {
      if ('data' in loginUserError) {
        // @ts-ignore
        const dataError = loginUserError.data.error;
        if (dataError && dataError === 'rate_limit') {
          setIsRateLimitError(true);
        }
        if (loginUserError.data) {
          // @ts-ignore
          const next_request = loginUserError.data.next_request;
          if (next_request) {
            const secsToResend = Math.floor(Math.abs(Date.now() / 1000 - next_request));
            let resendtimer = `${secsToResend} seconds`;

            if (secsToResend > 60) {
              resendtimer = `${Math.floor(secsToResend / 60)} minutes`;
            }

            setTimeToReset(resendtimer);
            setTimeout(() => setIsRateLimitError(false), secsToResend * 1000);
          }
        }
      }
    }
  }, [isErrorLoginUser]);

  return (
    <Container>
      <div style={{ marginBottom: 95 }}>
        <FidoLogo />
      </div>
      <div style={{ marginBottom: 24 }}>
        <ChangePassword />
      </div>
      <Spacer centered>
        <Title style={{ fontSize: 40, fontWeight: 600, color: 'black' }}>Enter the verification code</Title>
        <Subtitle>
          <Typography
            style={{
              fontSize: 14,
              fontWeight: 400,
              color: '#616161',
              marginBottom: 24,
            }}
          >
            We ask you for the code based on your two-factor authentication preferences.
          </Typography>
        </Subtitle>
      </Spacer>
      <Box>
        <form>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2">Verification Code</Typography>
            <div style={{ marginLeft: '88px' }}>
              {[0, 1, 2, 3, 4, 5].map((e) => (
                <TextField
                  key={e}
                  onChange={(s) => {
                    const smsCodeClone = [...smsCode];
                    smsCodeClone[e] = s.target.value;
                    setSmsCode([...smsCodeClone]);
                    if (s.target.value !== '') setIdToFocus(e + 1);
                    else setIdToFocus(e);
                  }}
                  inputRef={(input) => input && e === idToFocus && input.focus()}
                  style={{ width: 38, marginRight: 10 }}
                  inputProps={{ maxLength: 1 }}
                />
              ))}
            </div>
          </div>
          {isError && (
            <div
              style={{
                display: 'flex',
                margin: '12px auto 0px',
                maxWidth: '496px',
              }}
            >
              <Error color="error" style={{ marginRight: '10px' }} />
              <Typography variant="body2" style={{ color: 'red' }}>
                Invalid code or auth state for the user.
              </Typography>
            </div>
          )}
          {isRateLimitError && (
            <div
              style={{
                display: 'flex',
                margin: '12px auto 0px',
                maxWidth: '496px',
              }}
            >
              <Error color="error" style={{ marginRight: '10px' }} />
              <Typography variant="body2" style={{ color: 'red' }}>
                {`Too many attempts. Wait ${timeToReset || 'a few minutes'} before requesting a new
                          verification code.`}
              </Typography>
            </div>
          )}

          <Typography
            style={{
              color: isRateLimitError || disableResend ? theme.palette.secondary.light : theme.palette.primary.main,
              pointerEvents: isRateLimitError || disableResend ? 'none' : 'all',
              marginTop: '12px',
              cursor: isRateLimitError || disableResend ? 'not-allowed' : 'pointer',
            }}
            variant="body2"
            onClick={async () => {
              setDisableResend(true);
              setTimeout(() => setDisableResend(false), 10000);
              await loginUser({
                username: payload['username'] ?? '',
                password: payload['password'] ?? '',
              });
            }}
          >
            Resend code
          </Typography>

          <div
            style={{
              display: 'flex',
              margin: '0 auto',
              maxWidth: '491px',
              marginTop: '12px',
            }}
          >
            <FormControlLabel
              control={<Checkbox onChange={() => setRememberMe(!rememberMe)} value={rememberMe} />}
              label={<Typography variant="body1">Remember this device</Typography>}
            />
          </div>
        </form>

        <CustomButton
          variant="contained"
          color="primary"
          style={{
            backgroundColor: smsCode.length < 6 ? '' : 'black',
            color: 'white',
            marginTop: 32,
          }}
          disabled={smsCode.length < 6}
          onClick={() =>
            sendMFA({
              code: smsCode.join(''),
              remember_me: rememberMe,
              session: (newSessionToken || payload['session']) ?? '',
              token: payload['token'] ?? '',
            })
          }
        >
          Sign in
        </CustomButton>
      </Box>
    </Container>
  );
};

export default VerificationCodePage;
